import { supabase } from '../lib/supabase.js';

export const userService = {
  /**
   * Get all users
   * Used by admin dashboard to list and manage users
   */
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('users')
        ?.select('*, user_roles(roles(name))')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(user => ({
        id: user?.id,
        email: user?.email,
        firstName: user?.first_name,
        lastName: user?.last_name,
        username: user?.username,
        bio: user?.bio,
        location: user?.location,
        avatarUrl: user?.avatar_url,
        createdAt: user?.created_at,
        updatedAt: user?.updated_at,
        role: user?.user_roles?.map(ur => ur.roles?.name).filter(Boolean).join(', ')
      })) || [];
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  },

  /**
   * Get current user's profile
   * Auto-creates profile if it doesn't exist yet
   */
  async getCurrentUserProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Try to get existing profile
      let { data, error } = await supabase?.from('users')?.select('*, user_roles(roles(name))')?.eq('id', user?.id)?.single();

      // If profile doesn't exist, create it
      if (error?.code === 'PGRST116' || !data) {
        console.log('Profile not found, creating new profile...');
        
        // Create new profile from auth user data
        const newProfile = {
          id: user?.id,
          email: user?.email,
          first_name: user?.user_metadata?.first_name || '',last_name: user?.user_metadata?.last_name || '',password_hash: 'managed_by_supabase_auth'
        };

        const { data: createdProfile, error: createError } = await supabase
          ?.from('users')
          ?.insert(newProfile)
          ?.select('*')
          ?.single();

        if (createError) {
          console.error('Failed to create profile:', createError);
          throw new Error(`Failed to create profile: ${createError?.message}`);
        }

        data = createdProfile;
      } else if (error) {
        throw error;
      }

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        email: data?.email,
        firstName: data?.first_name,
        lastName: data?.last_name,
        username: data?.username,
        bio: data?.bio,
        location: data?.location,
        avatarUrl: data?.avatar_url,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        role: data?.user_roles?.map(ur => ur.roles?.name).filter(Boolean).join(', ')
      };
    } catch (error) {
      console.error('Error in getCurrentUserProfile:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(profileData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case for database
    const updateData = {};
    if (profileData?.firstName !== undefined) updateData.first_name = profileData?.firstName;
    if (profileData?.lastName !== undefined) updateData.last_name = profileData?.lastName;
    if (profileData?.username !== undefined) updateData.username = profileData?.username;
    if (profileData?.bio !== undefined) updateData.bio = profileData?.bio;
    if (profileData?.location !== undefined) updateData.location = profileData?.location;
    if (profileData?.avatarUrl !== undefined) updateData.avatar_url = profileData?.avatarUrl;

    const { data, error } = await supabase?.from('users')?.update(updateData)?.eq('id', user?.id)?.select('*')?.single();

    if (error) {
        // Check for unique constraint violation (code 23505)
        if (error?.code === '23505') {
            // Check if the error specifically relates to the username unique constraint
            if (error?.message?.includes('users_username_key') || error?.details?.includes('username')) {
                throw new Error('Username already taken. Please choose a different one.');
            }
            if (error?.message?.includes('users_email_key') || error?.details?.includes('email')) {
                throw new Error('Email already taken. Please use a different one.');
            }
        }
        throw error; // Re-throw any other errors
    }

    // Convert back to camelCase
    return {
      id: data?.id,
      email: data?.email,
      firstName: data?.first_name,
      lastName: data?.last_name,
      username: data?.username,
      bio: data?.bio,
      location: data?.location,
      avatarUrl: data?.avatar_url,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Create a user profile
   */
  async createUserProfile(userId, email, userData = null) {
    try {
      // First, check if the profile already exists (to avoid duplicate inserts if trigger worked)
      const { data: existingProfile, error: fetchError } = await supabase
        ?.from('users')
        ?.select('id')
        ?.eq('id', userId)
        ?.maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error checking for existing profile:', fetchError);
      }

      if (!existingProfile) {
        console.log('Profile not found for userId, creating manually:', userId);
        
        // Use provided userData or fall back to current session
        let profileUser = userData;
        if (!profileUser) {
           const { data: { user } } = await supabase.auth.getUser();
           profileUser = user;
        }
        
        const newProfile = {
          id: userId,
          email: email,
          first_name: profileUser?.user_metadata?.first_name || '',
          last_name: profileUser?.user_metadata?.last_name || '',
          created_at: new Date().toISOString()
        };

        const { error: insertError } = await supabase
          ?.from('users')
          ?.insert(newProfile);

        if (insertError) {
          console.error('Failed to manually create profile:', insertError);
          // If it's a conflict error (23505), someone else (like the trigger) created it in the meantime
          if (insertError.code !== '23505') {
            throw insertError;
          }
        } else {
          console.log('User profile created successfully for:', userId);
        }
      } else {
        console.log('User profile already exists for userId:', userId);
      }
      
      return { id: userId, email: email };
    } catch (error) {
      console.error('Error in client-side createUserProfile:', error);
      throw error;
    }
  },

  /**
   * Get user's garage/tool shed (models in their collection)
   * Now gets userId internally from Supabase auth if not provided
   */
  async getUserGarage(userId = null) {
    try {
      // Get userId from auth if not provided
      let effectiveUserId = userId;
      if (!effectiveUserId) {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) throw new Error('Not authenticated');
        effectiveUserId = user?.id;
      }

      const { data, error } = await supabase
        ?.from('user_garage')
        ?.select(`
          id,
          added_at,
          notes,
          products (
            id,
            name,
            description,
            price,
            thumbnail_path,
            manufacturer_id,
            manufacturers (
              id,
              name
            )
          )
        `)
        ?.eq('user_id', effectiveUserId)
        ?.order('added_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(item => ({
        id: item?.id,
        addedAt: item?.added_at,
        notes: item?.notes,
        product: {
          id: item?.products?.id,
          name: item?.products?.name,
          description: item?.products?.description,
          price: item?.products?.price,
          thumbnailPath: item?.products?.thumbnail_path,
          manufacturerId: item?.products?.manufacturer_id,
          manufacturerName: item?.products?.manufacturers?.name
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching user garage:', error);
      throw error;
    }
  },

  /**
   * Add product to user's garage
   */
  async addToGarage(productId, notes = '') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_garage')?.insert({
        user_id: user?.id,
        product_id: productId,
        notes: notes
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      productId: data?.product_id,
      addedAt: data?.added_at,
      notes: data?.notes
    };
  },

  /**
   * Remove product from user's garage
   */
  async removeFromGarage(garageItemId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('user_garage')?.delete()?.eq('id', garageItemId)?.eq('user_id', user?.id);

    if (error) throw error;
  },

  /**
   * Update garage item notes
   */
  async updateGarageNotes(garageItemId, notes) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_garage')?.update({ notes: notes })?.eq('id', garageItemId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      productId: data?.product_id,
      addedAt: data?.added_at,
      notes: data?.notes
    };
  },

  /**
   * Record a user login session or update session activity
   */
  async recordActivity(userId, userAgent, activityType, activityDetails, productId = null, componentId = null, app = 'opengadget') {
    try {
      let effectiveUserId = userId || null;
      let anonId = null;

      // Handle anonymous tracking
      if (!effectiveUserId) {
        anonId = localStorage.getItem('anon_session_id');
        if (!anonId) {
           anonId = 'anon_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
           localStorage.setItem('anon_session_id', anonId);
        }
      }

      let geoData = null;
      try {
          const cached = sessionStorage.getItem('geo_data');
          if (cached) geoData = JSON.parse(cached);
      } catch (e) {}

      let city = geoData?.city || null;
      let region = geoData?.region || null;
      let country = geoData?.country || null;
      let latitude = geoData?.latitude || null;
      let longitude = geoData?.longitude || null;
      let deviceType = geoData?.deviceType || null;
      let browserType = geoData?.browserType || null;
      let osType = geoData?.osType || null;

      // Fetch geo and device data if it's a Login/Signup, OR if it's the very first time tracking this session
      // Don't re-attempt if it failed recently to prevent 2 second hang per action
      const geoFailed = sessionStorage.getItem('geo_failed') === 'true';
      const needsGeo = !geoFailed && (activityType === 'Login' || activityType === 'Signup' || !geoData);

      if (needsGeo) {
        // Parse User Agent
        const { UAParser } = await import('ua-parser-js');
        const parser = new UAParser();
        parser.setUA(userAgent);
        const result = parser.getResult();

        deviceType = result.device.type || 'desktop';
        browserType = result.browser.name || 'Unknown';
        osType = result.os.name || 'Unknown';

        // Fetch Location gracefully with multi-provider fallback (ipapi.co -> ipwho.is -> freeipapi.com)
        try {
          let fetchedGeo = null;

          // Provider 1: ipapi.co
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);
            const geoRes = await fetch('https://ipapi.co/json/', { signal: controller.signal });
            clearTimeout(timeoutId);
            if (geoRes.ok) {
              const data = await geoRes.json();
              if (!data.error && (data.city || data.country_name)) {
                fetchedGeo = {
                  city: data.city || null,
                  region: data.region || null,
                  country: data.country_name || null,
                  latitude: data.latitude || null,
                  longitude: data.longitude || null
                };
              }
            }
          } catch (e) {}

          // Provider 2: ipwho.is (CORS friendly, free)
          if (!fetchedGeo) {
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 2000);
              const geoRes = await fetch('https://ipwho.is/', { signal: controller.signal });
              clearTimeout(timeoutId);
              if (geoRes.ok) {
                const data = await geoRes.json();
                if (data.success) {
                  fetchedGeo = {
                    city: data.city || null,
                    region: data.region || null,
                    country: data.country || null,
                    latitude: data.latitude || null,
                    longitude: data.longitude || null
                  };
                }
              }
            } catch (e) {}
          }

          // Provider 3: freeipapi.com
          if (!fetchedGeo) {
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 2000);
              const geoRes = await fetch('https://freeipapi.com/api/json', { signal: controller.signal });
              clearTimeout(timeoutId);
              if (geoRes.ok) {
                const data = await geoRes.json();
                if (data.cityName || data.countryName) {
                  fetchedGeo = {
                    city: data.cityName || null,
                    region: data.regionName || null,
                    country: data.countryName || null,
                    latitude: data.latitude || null,
                    longitude: data.longitude || null
                  };
                }
              }
            } catch (e) {}
          }

          if (fetchedGeo) {
            city = fetchedGeo.city;
            region = fetchedGeo.region;
            country = fetchedGeo.country;
            latitude = fetchedGeo.latitude;
            longitude = fetchedGeo.longitude;

            // Cache it in sessionStorage so we don't hit the API on every page view
            sessionStorage.setItem('geo_data', JSON.stringify({
                city, region, country, latitude, longitude, deviceType, browserType, osType
            }));
          } else {
            sessionStorage.setItem('geo_failed', 'true');
          }
        } catch (geoError) {
          sessionStorage.setItem('geo_failed', 'true');
          console.warn('Geolocation fetch skipped/failed:', geoError);
        }
      }

      // Fetch referrer dynamically to track incoming session traffic
      const referrerUrl = typeof document !== 'undefined' ? (document.referrer || 'Direct') : 'Direct';

      // Use reliable database RPC
      const validProductId = (productId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId)) ? productId : null;
      const validComponentId = (componentId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(componentId)) ? componentId : null;

      const { error } = await supabase.rpc('record_user_activity', {
          p_user_id: effectiveUserId,
          p_user_agent: userAgent,
          p_activity_type: activityType,
          p_activity_details: activityDetails,
          p_city: city,
          p_region: region,
          p_country: country,
          p_latitude: latitude,
          p_longitude: longitude,
          p_device_type: deviceType,
          p_browser_type: browserType,
          p_os_type: osType,
          p_product_id: validProductId,
          p_anonymous_id: anonId,
          p_component_id: validComponentId,
          p_referrer: referrerUrl,
          p_app: app || 'opengadget'
      });
      
      if (error) throw error;
      
    } catch (error) {
      console.warn('Activity recording failed or skipped:', error.message);
    }
  }
            };